"use client";

import { getFallbackMarblePhoneNumber } from "@/lib/modules/communications/utils";
import { getMarblePhoneNumber } from "@/lib/modules/states/phone";
import { State } from "@prisma/client";
import { useEffect, useState } from "react";

const getRequestKey = (state: State | undefined, formatted: boolean) =>
  `${state ?? ""}:${formatted}`;

/**
 * Client-side Marble phone number for a state. Patient-facing copy renders the
 * standard NY emergency fallback until the cached state config value arrives;
 * callers can opt out when they must wait for the configured caller ID.
 * Resolved values are keyed by request so changing state never shows the
 * previously resolved state's number.
 */
export function useMarblePhoneNumber({
  state,
  formatted = false,
  useFallbackWhileLoading = true,
}: {
  state?: State;
  formatted?: boolean;
  useFallbackWhileLoading?: boolean;
}): string {
  const [resolved, setResolved] = useState<{
    requestKey: string;
    phoneNumber: string;
  } | null>(null);

  useEffect(() => {
    let isCancelled = false;

    void requestMarblePhoneNumber(state, formatted).then((value) => {
      if (!isCancelled) {
        setResolved({
          requestKey: getRequestKey(state, formatted),
          phoneNumber: value,
        });
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [state, formatted]);

  if (resolved?.requestKey === getRequestKey(state, formatted)) {
    return resolved.phoneNumber;
  }

  return useFallbackWhileLoading
    ? getFallbackMarblePhoneNumber({ formatted })
    : "";
}

const phoneNumberRequests = new Map<string, Promise<string>>();

function requestMarblePhoneNumber(
  state: State | undefined,
  formatted: boolean,
) {
  const requestKey = getRequestKey(state, formatted);
  const existing = phoneNumberRequests.get(requestKey);
  if (existing) {
    return existing;
  }

  const request = getMarblePhoneNumber({ state, formatted }).catch((error) => {
    phoneNumberRequests.delete(requestKey);
    throw error;
  });
  phoneNumberRequests.set(requestKey, request);
  return request;
}
